import React from 'react';
import Grid from '@material-ui/core/Grid';
import {
  Create,
  SimpleForm,
  SimpleFormIterator,
  TextInput,
  ArrayInput,
  AutocompleteInput,
  ReferenceInput,
  SelectInput,
  FileField,
  FileInput,
  FormDataConsumer,
  BooleanInput,
  required,
  maxLength,
  number,
} from 'react-admin';
import Toolbar from '../resource-toolbar';
import WithTooltip from '../with-tooltip';
import { CategoryInput } from '../inputs';
import { LanguageChanger } from '../fields';
import { useServerValidation, useLanguageChange, getStyle } from '../../helpers';
import { PROD_STATUSES, LANGUAGES, LICENSE_TYPES, PRICE_TIER } from '../../constants';

export default (props) => {
  const { errors, onFailure } = useServerValidation();
  const [defaultLanguage] = LANGUAGES;
  const { language, onLanguageChange } = useLanguageChange(defaultLanguage);
  return (
    <Grid item lg={6} md={8} xs={12}>
      <Create {...props} onFailure={onFailure} title="Create Artwork">
        <SimpleForm toolbar={<Toolbar resourceName="products" />}>
          {(props.permissions === 'MASTER_ADMIN' || props.permissions === 'ADMIN') && (
            <>
              <ReferenceInput
                source="provider_id"
                reference="users"
                filter={{ role_id: [0,1,2] }}
                sort={{ field: 'first_name', order: 'ASC' }}
                fullWidth
                validate={required()}
              >
                <AutocompleteInput optionText={(record) => record ? `${record.first_name} ${record.last_name}` : ''} />
              </ReferenceInput>
              <SelectInput
                source="status"
                translateChoice={false}
                choices={PROD_STATUSES}
                fullWidth
                defaultValue="New"
                validate={required()}
              />
              <SelectInput
                source="license_type"
                translateChoice={false}
                choices={LICENSE_TYPES}
                fullWidth
                defaultValue="Paid"
                validate={required()}
              />
            </>
          )}
          <ReferenceInput
            source="host_id"
            reference="hosts"
            fullWidth
            sort={{ field: 'name', order: 'ASC' }}
            perPage={1000}
          >
            <AutocompleteInput optionText="name" />
          </ReferenceInput>
          <ReferenceInput
            source="artist_id"
            reference="artists"
            fullWidth
            validate={required()}
            sort={{ field: 'first_name', order: 'ASC' }}
            perPage={1000}
          >
            <AutocompleteInput optionText={(record) => record ? `${record.first_name} ${record.last_name}` : ''} />
          </ReferenceInput>
          <TextInput source="name" label="Artwork Name" fullWidth validate={[required(), maxLength(255)]} />
          <WithTooltip tooltipText="Allowed formats: JPG, PNG, MP4, AVI, M4V, WEBM">
            <FileInput source="files" label="Media File" accept="video/*, image/*">
              <FileField source="url" title="Artwork Media File" />
            </FileInput>
          </WithTooltip>
          <ReferenceInput source="art_type_id" reference="art-types" fullWidth>
            <SelectInput source="name" validate={required()}/>
          </ReferenceInput>
          <ArrayInput source="categories" validate={required()}>
            <SimpleFormIterator>
              <FormDataConsumer>
                {({ formData, getSource, ...rest }) => {
                  return (
                    <CategoryInput
                      source={getSource('categories')}
                      getSource={getSource}
                      formData={formData}
                      errors={errors}
                      validate={required()}
                      {...rest}
                    />
                  );
                }}
              </FormDataConsumer>
            </SimpleFormIterator>
          </ArrayInput>
          <TextInput source="location" fullWidth />
          <TextInput source="year" fullWidth validate={number()}/>
          {(props.permissions === 'MASTER_ADMIN' || props.permissions === 'ADMIN') && (
            <>
              <SelectInput
                source="price_tier"
                translateChoice={false}
                choices={PRICE_TIER}
                fullWidth
              />
              <BooleanInput source="wall" defaultValue={true} />
              <BooleanInput source="staff_pick" />
            </>
          )}
          <LanguageChanger language={language} onLanguageChange={onLanguageChange} />
          <TextInput style={getStyle(language, '_en')} minRows={5} multiline source="description" fullWidth validate={maxLength(10000)} />
          <TextInput style={getStyle(language, '_fr')} minRows={5} multiline source="description_fr" fullWidth validate={maxLength(10000)}/>
          <TextInput style={getStyle(language, '_jp')} minRows={5} multiline source="description_jp" fullWidth validate={maxLength(10000)}/>
          <TextInput style={getStyle(language, '_cn')} minRows={5} multiline source="description_cn" fullWidth validate={maxLength(10000)}/>
          <TextInput style={getStyle(language, '_de')} minRows={5} multiline source="description_de" fullWidth validate={maxLength(10000)}/>
          <TextInput style={getStyle(language, '_kr')} minRows={5} multiline source="description_kr" fullWidth validate={maxLength(10000)}/>
          <TextInput style={getStyle(language, '_es')} minRows={5} multiline source="description_es" fullWidth validate={maxLength(10000)}/>
          <WithTooltip tooltipText="Tags separated by comma (,)">
            <TextInput source="tags" fullWidth />
          </WithTooltip>
        </SimpleForm>
      </Create>
    </Grid>
  );
};
